import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { PatientSectionLayout } from './PatientSectionLayout';
import { HealthVitalsInsightsView } from '../../components/health/HealthVitalsInsightsView';
import {
  nutritionProfileService,
  CurrentNutritionState,
  derivedMetricsService,
  DerivedMetricsSummaryResponse,
  DerivedMetricSummaryItem,
} from '../../services/nutrition/nutritionProfileService';
import { VITAL_BACKED_DERIVED_METRICS } from '../../services/nutrition/derivedMetricsVitals';

const PatientInsightsPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const numericPatientId = patientId ? Number(patientId) : NaN;
  const validId = !Number.isNaN(numericPatientId) && numericPatientId > 0;

  const [derivedState, setDerivedState] = useState<CurrentNutritionState | null>(null);
  const [derivedLoading, setDerivedLoading] = useState(false);
  const [derivedError, setDerivedError] = useState<string | null>(null);
  const [derivedMetricsSummary, setDerivedMetricsSummary] = useState<DerivedMetricsSummaryResponse | null>(null);
  const [derivedMetricsLoading, setDerivedMetricsLoading] = useState(false);
  const [derivedMetricsError, setDerivedMetricsError] = useState<string | null>(null);
  const [insightsSubTab, setInsightsSubTab] = useState<'metrics' | 'scores'>('metrics');
  const [insightsEdits, setInsightsEdits] = useState<Record<string, number>>({});
  const [insightsSaving, setInsightsSaving] = useState(false);
  const [insightsEditMode, setInsightsEditMode] = useState(false);

  const filteredDerivedInsights = useMemo(() => {
    if (!derivedMetricsSummary?.items) return [];
    return derivedMetricsSummary.items
      .filter((item) => {
        if (insightsSubTab === 'metrics') {
          return item.category !== 'Composite Score' && !item.category?.toLowerCase().includes('composite');
        }
        return item.category === 'Composite Score' || item.category?.toLowerCase().includes('composite');
      })
      .sort((a, b) => a.metric_key.localeCompare(b.metric_key));
  }, [derivedMetricsSummary, insightsSubTab]);

  const isCompositeScore = (item: DerivedMetricSummaryItem) =>
    item.category === 'Composite Score' || (item.category?.toLowerCase() ?? '').includes('composite');

  const editableInsightsForVitals = useMemo(
    () =>
      filteredDerivedInsights
        .filter((item) => !isCompositeScore(item))
        .filter((item) => insightsEdits[item.metric_key] !== undefined)
        .map((item) => {
          const editValue = insightsEdits[item.metric_key];
          const valueToSave = typeof editValue === 'number' && Number.isFinite(editValue) ? editValue : null;
          const vitalType = VITAL_BACKED_DERIVED_METRICS[item.metric_key];
          if (vitalType === undefined || valueToSave == null) return null;
          return { item, editValue: valueToSave, vitalType };
        })
        .filter((entry): entry is { item: DerivedMetricSummaryItem; editValue: number; vitalType: string } => entry !== null),
    [filteredDerivedInsights, insightsEdits]
  );

  const editableInsightsForScores = useMemo(
    () =>
      filteredDerivedInsights
        .filter((item) => isCompositeScore(item))
        .filter((item) => insightsEdits[item.metric_key] !== undefined)
        .map((item) => {
          const editValue = insightsEdits[item.metric_key];
          const valueToSave = typeof editValue === 'number' && Number.isFinite(editValue) ? editValue : null;
          if (valueToSave == null) return null;
          return { item, valueToSave };
        })
        .filter((entry): entry is { item: DerivedMetricSummaryItem; valueToSave: number } => entry !== null),
    [filteredDerivedInsights, insightsEdits]
  );

  const editableInsightsCount = editableInsightsForVitals.length + editableInsightsForScores.length;

  useEffect(() => {
    if (!validId) return;
    let cancelled = false;
    const load = async () => {
      setDerivedLoading(true);
      setDerivedError(null);
      try {
        const state = await nutritionProfileService.getCurrentStateForPatient(numericPatientId);
        if (!cancelled) setDerivedState(state);
      } catch (e: any) {
        if (!cancelled) setDerivedError(e?.response?.data?.detail || e?.message || 'Failed to load insights');
      } finally {
        if (!cancelled) setDerivedLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [numericPatientId, validId]);

  useEffect(() => {
    if (!validId) return;
    let cancelled = false;
    setDerivedMetricsSummary(null);
    setDerivedMetricsError(null);
    const load = async () => {
      setDerivedMetricsLoading(true);
      try {
        const summary = await derivedMetricsService.getSummary(numericPatientId);
        if (!cancelled) setDerivedMetricsSummary(summary);
      } catch (e: any) {
        if (!cancelled) setDerivedMetricsError(e?.response?.data?.detail || e?.message || 'Failed to load metrics');
      } finally {
        if (!cancelled) setDerivedMetricsLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [numericPatientId, validId]);

  const handleInsightsSave = useCallback(async () => {
    if (editableInsightsCount === 0 || !validId) return;
    setInsightsSaving(true);
    try {
      const recordedAt = new Date().toISOString();
      let message = '';
      if (editableInsightsForVitals.length > 0) {
        const payloadEntries = editableInsightsForVitals.map(({ item, editValue, vitalType }) => ({
          metric_type: vitalType,
          value: editValue,
          unit: item.unit || item.reference_unit || '',
          recorded_at: recordedAt,
          notes: `Doctor insights edit (${item.display_name})`,
          source_device: 'Doctor Portal Insights',
        }));
        const res = await derivedMetricsService.saveInsightsVitals(numericPatientId, { entries: payloadEntries });
        message = res.message || 'Vitals saved.';
      }
      if (editableInsightsForScores.length > 0) {
        const scoreEntries = editableInsightsForScores.map(({ item, valueToSave }) => ({
          score_name: item.metric_key,
          value: valueToSave,
          recorded_at: recordedAt,
        }));
        const scoreRes = await derivedMetricsService.saveInsightsDerivedScores(numericPatientId, { entries: scoreEntries });
        const scoreMsg = scoreRes.processed_count ? `${scoreRes.processed_count} score(s) saved.` : scoreRes.message;
        message = message ? `${message} ${scoreMsg}` : scoreMsg;
      }
      setInsightsEditMode(true);
      setInsightsEdits((prev) => {
        const next = { ...prev };
        editableInsightsForVitals.forEach(({ item }) => delete next[item.metric_key]);
        editableInsightsForScores.forEach(({ item }) => delete next[item.metric_key]);
        return next;
      });
    } catch (e: any) {
      // error handled by UI
    } finally {
      setInsightsSaving(false);
    }
  }, [editableInsightsCount, editableInsightsForVitals, editableInsightsForScores, numericPatientId, validId]);

  const handleCancelInsightsEdit = useCallback(() => {
    setInsightsEdits({});
    setInsightsEditMode(false);
  }, []);

  return (
    <PatientSectionLayout section="insights">
      <section className="dashboard-section patient-tabs-section">
        {validId ? (
          <HealthVitalsInsightsView
            items={filteredDerivedInsights}
            loading={derivedLoading || derivedMetricsLoading}
            error={derivedError || derivedMetricsError}
            insightsSubTab={insightsSubTab}
            onInsightsSubTabChange={setInsightsSubTab}
            editMode={insightsEditMode}
            onEditModeChange={setInsightsEditMode}
            edits={insightsEdits}
            onEditsChange={setInsightsEdits}
            onSave={handleInsightsSave}
            onCancel={handleCancelInsightsEdit}
            saving={insightsSaving}
            saveButtonLabel={editableInsightsCount > 0 ? `Save ${editableInsightsCount} metric${editableInsightsCount === 1 ? '' : 's'}` : undefined}
            message={null}
            messageLevel="info"
            showMessageBox={false}
            showSourceColumn
          />
        ) : null}
      </section>
    </PatientSectionLayout>
  );
};

export default PatientInsightsPage;
